import React, { useState, useEffect } from 'react'
import { SelectChangeEvent } from '@mui/material/Select'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import UseAlert from '../../components/common/useAlerte'
import { addProjet } from '../../redux/projets/projetActions'
import { getMissions } from '../../redux/missions/missionActions'
import SelectPriority from '../../components/common/SelectPriority'
import SelectMission from '../../components/common/SelectMission'
import DateSelector from '../../components/common/Date'
import SelectTypeProjet from '../../components/common/SelectTypeProjet'
import SelectPipeline from '../pipeline/SelectPipeline'
import { useAppDispatch, AppDispatch } from '../../redux/store'
import SelectStatut from '../../components/common/SelectStatut'
import { createSanitizedObjectHandler } from '../../utils/inputHandlers'
import NumberInput from '../../components/common/NumberInput'
import QuickCreate, { QuickCreated, useQuickCreate } from '../../components/quickCreate/QuickCreate'
import { MissionSummaryCard } from '../../components/quickCreate/ParentSummaryCard'
import { useMissionBudget } from '../../components/quickCreate/parentInfo'
import { useNumberFormat } from '../../hooks/useNumberFormat'
import { QuickEntity } from '../../components/quickCreate/quickCreateErrors'
import {
  FormAside,
  FormField,
  FormFoot,
  FormGrid,
  FormLayout,
  FormPageHead,
  FormSection,
  FormShell,
} from '../../components/common/form'
import './addprojet-menu-fix.css'

const ProjetCreate = () => {
  const { idmission, id } = useParams<{ idmission: string; id: string }>()

  const dispatch: AppDispatch = useAppDispatch()
  const navigate = useNavigate()
  const { t } = useTranslation()

  const [alert, setAlert] = useState({
    open: false,
    message: '',
    status: 0,
  })
  const [projet, setProjet] = useState({
    ProjetName: '',
    Description: '',
    DateDebut: new Date(),
    DateFin: new Date(),
    Status: 'A faire',
    Priority: 3,
    Budget: 0,
    TypeProjet: 'Autres',
    Source: '',
    MissionId: idmission || '',
    PipelineId: id || '',
  })
  const handleInputChange = createSanitizedObjectHandler(setProjet, {
    ProjetName: 'text',
    Description: 'text',
    Source: 'text',
    Budget: 'numbers',
  })

  const handleBudgetChange = (value: number | null) => {
    setProjet((prevProjet) => ({
      ...prevProjet,
      Budget: value ?? 0,
    }))
  }

  const handleDateChange = (dates: [Date, Date] | null) => {
    if (dates) {
      const [startDate, endDate] = dates
      setProjet((prevProjet) => ({
        ...prevProjet,
        DateDebut: startDate,
        DateFin: endDate,
      }))
    }
  }

  const handlePipelineChange = (event: SelectChangeEvent<string>) => {
    setProjet((prev) => ({
      ...prev,
      PipelineId: event.target.value,
    }))
  }

  const handleMissionChange = (newMission: string) => {
    setProjet((prev) => ({
      ...prev,
      MissionId: newMission,
      PipelineId: '',
    }))
  }

  const qc = useQuickCreate()
  const missionBudget = useMissionBudget(projet.MissionId || undefined)
  const fmt = useNumberFormat()
  const handleQuickCreated = (kind: QuickEntity, created: QuickCreated) => {
    qc.close()
    qc.bump(kind)
    const id = created._id as string
    setProjet((prev) =>
      kind === 'mission' ? { ...prev, MissionId: id, PipelineId: '' } : { ...prev, PipelineId: id }
    )
  }

  const handlePriorityChange = (event: SelectChangeEvent<number>) => {
    setProjet((prev) => ({
      ...prev,
      Priority: Number(event.target.value),
    }))
  }

  const handleTypeProjet = (event: SelectChangeEvent<string>) => {
    setProjet((prev) => ({
      ...prev,
      TypeProjet: event.target.value,
    }))
  }

  const handleStatusChange = (event: SelectChangeEvent<string>) => {
    setProjet((prev) => ({
      ...prev,
      Status: event.target.value,
    }))
  }

  useEffect(() => {
    dispatch(getMissions({ page: 1, pageSize: 100 }))
  }, [dispatch])

  useEffect(() => {
    const className = 'addprojet-menus-zoom-fix'
    document.body.classList.add(className)
    return () => {
      document.body.classList.remove(className)
    }
  }, [])

  const handleCloseAlert = () => {
    const wasSuccess = alert.status === 200
    setAlert({
      ...alert,
      open: false,
    })
    if (wasSuccess) {
      if (projet.MissionId) {
        navigate(`/mission-details/${projet.MissionId}`)
      } else {
        navigate('/projets')
      }
    }
  }

  const submitLabel = t('buttons.add')

  const handleAddProjet = async () => {
    if (
      !projet.ProjetName ||
      !projet.Description ||
      !projet.DateDebut ||
      !projet.DateFin ||
      !projet.Status ||
      !projet.Priority ||
      !projet.TypeProjet
    ) {
      setAlert({
        open: true,
        message: t('errors.projectCreation.validation.requiredFields'),
        status: 400,
      })
      return
    }

    if (projet.Budget < 0) {
      setAlert({
        open: true,
        message: t('errors.projectCreation.validation.invalidBudget'),
        status: 400,
      })
      return
    }

    if (missionBudget.data && projet.Budget > missionBudget.data.remaining) {
      setAlert({
        open: true,
        message: t('parentLimits.budgetOver', {
          available: fmt.money(missionBudget.data.remaining),
        }),
        status: 400,
      })
      return
    }

    if (projet.DateFin < projet.DateDebut) {
      setAlert({
        open: true,
        message: t('errors.projectCreation.validation.endDateBeforeStart'),
        status: 400,
      })
      return
    }

    try {
      const formattedProjet = {
        ...projet,
        Priority: Number(projet.Priority),
        // Hidden on this form. The API still requires Source.
        Source: projet.Source || 'N/A',
      }

      const result = await dispatch(addProjet(formattedProjet)).unwrap()

      setAlert({
        open: true,
        message: result.message || t('alerts.projectCreation.success'),
        status: 200,
      })

      setProjet({
        ProjetName: '',
        Description: '',
        DateDebut: new Date(),
        DateFin: new Date(),
        Status: 'A faire',
        Priority: 3,
        Budget: 0,
        TypeProjet: 'Autres',
        Source: '',
        MissionId: '',
        PipelineId: '',
      })
    } catch (error) {
      setAlert({
        open: true,
        message: (error as string) || t('alerts.projectCreation.error'),
        status: 400,
      })
    }
  }

  return (
    <div className="page-content addprojet-page">
      <FormPageHead
        eyebrow={t('entityForms.project.createEyebrow', {
          defaultValue: 'Management · Projects',
        })}
        title={t('buttons.addProject', { defaultValue: 'New project' })}
        subtitle={t('entityForms.project.createSubtitle', {
          defaultValue:
            'Set up project identity, planning, budget and pipeline linkage.',
        })}
        backLabel={t('entityForms.project.backToList', {
          defaultValue: 'Back to projects',
        })}
        onBack={() => navigate('/projets')}
      />

      <UseAlert
        open={alert.open}
        message={alert.message}
        status={alert.status}
        onClose={handleCloseAlert}
      />

      <FormLayout
        main={
          <FormShell>
            <FormSection
              title={t('entityForms.project.identityTitle', {
                defaultValue: 'Identification',
              })}
            >
              <FormGrid cols={2}>
                <FormField label={t('Project Name')} required span>
                  <input
                    className="incyia-form-inp"
                    name="ProjetName"
                    value={projet.ProjetName}
                    onChange={handleInputChange as any}
                    required
                  />
                </FormField>
                <FormField label={t('Mission')} span>
                  <SelectMission
                    value={projet.MissionId}
                    onChange={handleMissionChange}
                    onCreateNew={qc.open('mission')}
                    reloadKey={qc.keyOf('mission')}
                  />
                </FormField>
                <FormField label={t('Pipeline')} span>
                  <SelectPipeline
                    value={projet.PipelineId}
                    onChange={handlePipelineChange}
                    MissionId={projet.MissionId}
                    onCreateNew={projet.MissionId ? qc.open('pipeline', projet.MissionId) : undefined}
                    reloadKey={qc.keyOf('pipeline')}
                  />
                </FormField>
                <FormField label={t('Description')} required span>
                  <textarea
                    className="incyia-form-inp"
                    name="Description"
                    value={projet.Description}
                    onChange={handleInputChange as any}
                    rows={4}
                    required
                  />
                </FormField>
              </FormGrid>
            </FormSection>

            <FormSection
              title={t('entityForms.project.planningTitle', {
                defaultValue: 'Planning and cost',
              })}
            >
              <FormGrid cols={2}>
                <FormField label={t('Project Type')}>
                  <SelectTypeProjet
                    value={projet.TypeProjet}
                    onChange={handleTypeProjet}
                  />
                </FormField>
                <FormField
                  label={t('entityForms.project.dates', {
                    defaultValue: 'Start & end dates',
                  })}
                  help={
                    projet.MissionId
                      ? t('parentLimits.missionNoDates')
                      : undefined
                  }
                >
                  {/* TODO(backend): Mission has no DateDebut/DateFin, so project dates cannot be bounded by the mission. */}
                  <DateSelector
                    placeholder={t('entityForms.datePlaceholder')}
                    onDateChange={handleDateChange}
                  />
                </FormField>
                <FormField label={t('Budget')} required>
                  <NumberInput
                    currency
                    min={0}
                    name="Budget"
                    value={projet.Budget}
                    onChange={handleBudgetChange}
                    required
                  />
                </FormField>
                <FormField label={t('Status')}>
                  <SelectStatut
                    value={projet.Status}
                    onChange={handleStatusChange}
                  />
                </FormField>
                <FormField label={t('Priority')}>
                  <SelectPriority
                    value={projet.Priority}
                    onChange={handlePriorityChange}
                  />
                </FormField>
              </FormGrid>
            </FormSection>

            <FormFoot
              note={t('actionCreate.aside.requiredMark', {
                defaultValue: '* Required',
              })}
              cancelLabel={t('buttons.cancel', { defaultValue: 'Cancel' })}
              submitLabel={submitLabel}
              onCancel={() => navigate('/projets')}
              onSubmit={handleAddProjet}
            />
          </FormShell>
        }
        aside={
          <FormAside
            title={t('actionCreate.aside.inThisForm', {
              defaultValue: 'In this form',
            })}
            tipTitle={t('actionCreate.aside.goodToKnow', {
              defaultValue: 'Good to know',
            })}
            tip={t('entityForms.project.tip', {
              defaultValue:
                'Pipeline and dates drive the Gantt view on the project detail page.',
            })}
            context={
              projet.MissionId ? (
                <>
                  <MissionSummaryCard
                    plain
                    info={missionBudget.data}
                    status={missionBudget.status}
                  />
                  <p className="incyia-aside-note incyia-aside-divider">
                    {t('parentLimits.stayInside')}
                  </p>
                </>
              ) : undefined
            }
            steps={[
              {
                title: t('entityForms.project.identityTitle', {
                  defaultValue: 'Identification',
                }),
                fieldCount: 4,
              },
              {
                title: t('entityForms.project.planningTitle', {
                  defaultValue: 'Planning and cost',
                }),
                fieldCount: 5,
              },
            ]}
            requiredCount={4}
            submitLabel={submitLabel}
            fieldsLabel={(n) =>
              t(
                n === 1
                  ? 'actionCreate.aside.fields_one'
                  : 'actionCreate.aside.fields_other',
                { count: n, defaultValue: `${n} fields` }
              )
            }
            requiredNote={(count, submit) =>
              t('actionCreate.aside.requiredNote', {
                count,
                submit,
                defaultValue: `${count} required before you can ${submit}.`,
              })
            }
          />
        }
      />
      <QuickCreate request={qc.request} onClose={qc.close} onCreated={handleQuickCreated} />
    </div>
  )
}

export default ProjetCreate
